import Modal from './modal.js';
import countries from 'i18n-iso-countries';
import enCountryLocale from 'i18n-iso-countries/langs/en.json';
import { updateMarketAttribute, waitForMarketData } from '../utils/cart-attributes.js';
import { createDebugger } from '../utils/debug.js';

countries.registerLocale(enCountryLocale);

const debug = createDebugger('GeolocationModal', false);

const SELECTORS = {
    CONTENT_TEMPLATE: '[data-content-template]',
    COUNTRY_WITH_FLAG_TEMPLATE: '[data-country-with-flag-template]',
    OPTION_DATA: '[data-redirect-option-data]',
    ACCEPT_BUTTON: '[data-geo-accept]'
};

class GeolocationModal extends Modal {
    constructor() {
        super();

        // Bind event handlers once to prevent memory leaks
        this.handleGeolocationRecommendation = this.handleGeolocationRecommendation.bind(this);
        this.handleCartAdd = this.handleCartAdd.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this.init();
    }

    disconnectedCallback() {
        // Clean up event listeners to prevent memory leaks
        document.removeEventListener('geolocation-recommendation', this.handleGeolocationRecommendation);
        document.removeEventListener('on:cart:add', this.handleCartAdd);

        if (super.disconnectedCallback) {
            super.disconnectedCallback();
        }
    }

    init() {
        debug.log('init');

        this.getGeolocationOptionData();

        this.shopCountryRedirectOption = this.getRedirectOption(window.Shopify.country);

        // Store in both old location (for compatibility) and new namespaced location
        window.shopCountryRedirectOption = this.shopCountryRedirectOption;
        if (window.shopifyMarketCart) {
            window.shopifyMarketCart.shopCountryRedirectOption = this.shopCountryRedirectOption;
        }

        // Dispatch event to notify that market data is ready
        document.dispatchEvent(
            new CustomEvent('market:ready', {
                detail: {
                    shopCountryRedirectOption: this.shopCountryRedirectOption
                }
            })
        );

        let sessionClosed = false;
        try {
            sessionClosed = sessionStorage.getItem('geolocation-modal-closed');
        } catch (error) {
            console.warn('[GeolocationModal] Failed to read sessionStorage:', error);
        }

        if (sessionClosed) {
            debug.log('init: geolocation-modal-closed');
            return;
        }

        // Use pre-bound handlers to prevent memory leaks
        document.addEventListener('geolocation-recommendation', this.handleGeolocationRecommendation);
        document.addEventListener('on:cart:add', this.handleCartAdd);
    }

    getGeolocationOptionData() {
        debug.log('getGeolocationOptionData');

        try {
            const redirectOptionData = this.querySelector(SELECTORS.OPTION_DATA);
            if (!redirectOptionData) {
                throw new Error(
                    `Missing geolocation data script (${SELECTORS.OPTION_DATA}) in geoloaction modal`
                );
            }

            const json = JSON.parse(redirectOptionData.textContent);

            this.redirectOptions = json.redirect_options;

            debug.log('this.redirectOptions', this.redirectOptions);

            if (!this.redirectOptions) {
                throw new Error('Missing redirect options in geoloaction data');
            }

            redirectOptionData.remove();
        } catch (error) {
            throw new Error(`Error getting geoloaction data: ${error}`, this);
        }
    }

    getRedirectOption(country) {
        const getPotentialRedirect = (redirectOptions) => {
            debug.log('getRedirectOption', country);
            debug.log('redirectOptions', redirectOptions);
            const redirectOptionByCountry = redirectOptions.find(
                (redirectOption) =>
                    redirectOption.countries &&
                    Array.isArray(redirectOption.countries) &&
                    redirectOption.countries.includes(country)
            );

            if (redirectOptionByCountry) {
                return redirectOptionByCountry;
            }

            return redirectOptions.find((redirectOption) => redirectOption.is_default);
        };

        const redirectOption = getPotentialRedirect(this.redirectOptions);

        if (this.shopCountryRedirectOption && this.shopCountryRedirectOption.name === redirectOption.name) {
            return null;
        }

        return redirectOption;
    }

    populateTemplate({ customerCountry, shopCountry, redirectOption }) {
        if (!this.contentTemplate) {
            this.contentTemplate = this.querySelector(SELECTORS.CONTENT_TEMPLATE);
            if (!this.contentTemplate) {
                throw new Error(
                    `Missing content template (${SELECTORS.CONTENT_TEMPLATE}s) in geoloaction modal`
                );
            }
        }

        const contentString = this.contentTemplate.innerHTML
            .replaceAll('[customer_country]', customerCountry)
            .replaceAll(
                '[customer_country_w_flag]',
                this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry)
            )
            .replaceAll('[shop_country]', shopCountry)
            .replaceAll('[shop_country_w_flag]', this.getCountryHTMlWithFlag(this.shopCountryRedirectOption))
            .replaceAll('[redirect_name]', redirectOption.name)
            .replaceAll('[redirect_short_name]', redirectOption.short_name)
            .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));

        debug.log('contentString', contentString);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentString;
        this.contentTemplate.parentNode.insertBefore(tempDiv.firstElementChild, this.contentTemplate);

        debug.log('tempDiv', tempDiv);

        this.contentTemplate.remove();

        // Replace variables in button text
        this.replaceVariablesInButtons({ customerCountry, shopCountry, redirectOption });
    }

    getCountryWithFlagHTML(imageUrl, name) {
        if (!this.countryWithFlagTemplate) {
            this.countryWithFlagTemplate = this.querySelector(SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE);
            if (!this.countryWithFlagTemplate) {
                throw new Error(
                    `Missing country with flag template (${SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE}s) in geoloaction modal`
                );
            }
        }

        const countryWithFlagTemplate = this.countryWithFlagTemplate.innerHTML
            .replaceAll('[image_url]', imageUrl)
            .replaceAll('[name]', name);

        this.countryWithFlagTemplate.remove();

        return countryWithFlagTemplate;
    }

    getCountryHTMlWithFlag(redirectOption) {
        return this.getCountryWithFlagHTML(redirectOption.image_url, redirectOption.name);
    }

    getCustomerCountryWithFlag(customerCountry, isoCode) {
        return this.getCountryWithFlagHTML(this.getCountryFlagUrlFromISO(isoCode), customerCountry);
    }

    replaceVariablesInButtons({ customerCountry, shopCountry, redirectOption }) {
        const acceptButton = this.querySelector(SELECTORS.ACCEPT_BUTTON);
        const declineButton = this.querySelector('[data-toggle-geolocation-modal]');

        if (acceptButton) {
            acceptButton.innerHTML = acceptButton.innerHTML
                .replaceAll('[customer_country]', customerCountry)
                .replaceAll(
                    '[customer_country_w_flag]',
                    this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry)
                )
                .replaceAll('[shop_country]', shopCountry)
                .replaceAll(
                    '[shop_country_w_flag]',
                    this.getCountryHTMlWithFlag(this.shopCountryRedirectOption)
                )
                .replaceAll('[redirect_name]', redirectOption.name)
                .replaceAll('[redirect_short_name]', redirectOption.short_name)
                .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));
        }

        if (declineButton) {
            declineButton.innerHTML = declineButton.innerHTML
                .replaceAll('[customer_country]', customerCountry)
                .replaceAll(
                    '[customer_country_w_flag]',
                    this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry)
                )
                .replaceAll('[shop_country]', shopCountry)
                .replaceAll(
                    '[shop_country_w_flag]',
                    this.getCountryHTMlWithFlag(this.shopCountryRedirectOption)
                )
                .replaceAll('[redirect_name]', redirectOption.name)
                .replaceAll('[redirect_short_name]', redirectOption.short_name)
                .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));
        }
    }

    getCountryFromISO(isoCode) {
        if (!isoCode) {
            throw new Error(`Invalid ISO code: ${isoCode}`);
        }

        const countryName = countries.getName(isoCode, 'en');
        debug.log('countryName', countryName);
        switch (countryName) {
            case 'United States of America':
                return 'United States';
            default:
                return countryName;
        }
    }

    getCountryFlagUrlFromISO(isoCode) {
        return `https://flagcdn.com/w160/${isoCode.toLowerCase()}.png`;
    }

    handleGeolocationRecommendation(event) {
        debug.log('handleGeolocationRecommendation', event);

        this.recommendedCountry = event.detail.recommendedCountry;

        const redirectOption = this.getRedirectOption(this.recommendedCountry);
        debug.log('redirectOption', redirectOption);
        if (redirectOption) {
            this.populateTemplate({
                customerCountry: this.getCountryFromISO(this.recommendedCountry),
                shopCountry: this.shopCountryRedirectOption.name,
                redirectOption: redirectOption
            });

            debug.log('recommendedCountry', this.recommendedCountry);

            this.delegateClick(SELECTORS.ACCEPT_BUTTON, (match, event) => {
                this.handleAcceptButtonClick({
                    redirectOption
                });
            });

            this.open();
            debug.log('this', this);
        }
    }

    handleAcceptButtonClick({ redirectOption }) {
        try {
            // Update the market attribute before redirecting
            const marketName = redirectOption.short_name || redirectOption.name;
            const marketUrl = redirectOption.redirect_url;

            // Dispatch market changed event
            document.dispatchEvent(
                new CustomEvent('market:changed', {
                    detail: {
                        market: marketName,
                        market_url: marketUrl
                    }
                })
            );

            const { pathname, searchParams } = this.getCurrentPathAndParams();
            const redirectUrl = new URL(redirectOption.redirect_url);

            // Set the pathname (without query params)
            const basePath = redirectUrl.pathname === '/' ? '' : redirectUrl.pathname;
            // Ensure proper path separator between base path and current pathname
            const separator = basePath && !basePath.endsWith('/') && !pathname.startsWith('/') ? '/' : '';
            redirectUrl.pathname = basePath + separator + pathname;

            // Add existing search params from current URL
            searchParams.forEach((value, key) => {
                redirectUrl.searchParams.set(key, value);
            });

            // Add country parameter
            if (this.recommendedCountry) {
                redirectUrl.searchParams.set('country', this.recommendedCountry);
            }

            debug.log('handleAcceptButtonClick', {
                redirectOption,
                pathname,
                searchParams,
                redirectUrl: redirectUrl.toString()
            });

            window.location.href = redirectUrl.toString();
        } catch (error) {
            debug.error('handleAcceptButtonClick', error);
            window.location.href = redirectOption.redirect_url;
        }
    }

    getCurrentPathAndParams() {
        const rootpath = window.Shopify.routes.root;
        const pathname = window.location.pathname.replace(rootpath, '');
        const searchParams = new URLSearchParams(window.location.search);

        return { pathname, searchParams };
    }

    onClose() {
        try {
            sessionStorage.setItem('geolocation-modal-closed', 'true');
        } catch (error) {
            console.warn('[GeolocationModal] Failed to save session state:', error);
        }
    }

    async handleCartAdd(event) {
        debug.log('handleCartAdd', event);

        try {
            // Wait for market data if not ready yet
            let redirectOption = this.shopCountryRedirectOption;

            if (!redirectOption) {
                debug.log('Waiting for market data...');
                redirectOption = await waitForMarketData(3000); // 3 second timeout
            }

            const marketName = redirectOption.short_name || redirectOption.name;
            const marketUrl = redirectOption.redirect_url;

            if (!marketUrl) {
                console.error('[GeolocationModal] Missing redirect_url');
                return;
            }

            // Update market attribute (uses global lock to prevent race conditions)
            await updateMarketAttribute(marketName, marketUrl);
            debug.log('Market attribute ensured on cart add');
        } catch (error) {
            debug.error('Failed to update market on cart add:', error);
        }
    }

    onThemeEditorLoad() {
        const redirectOption = this.redirectOptions.find((redirectOption) => redirectOption.is_default);
        this.recommendedCountry = 'LT'; // Set for theme editor preview
        this.populateTemplate({
            customerCountry: this.getCountryFromISO('LT'),
            shopCountry: this.shopCountryRedirectOption.name,
            redirectOption: redirectOption
        });
    }
}

customElements.define('geolocation-modal', GeolocationModal);
